/**
 * adminExamPurchaseStore — Exam Purchases slice of the admin store (Zustand).
 * Exposes the exam-purchases + exam-dashboard endpoints.
 *
 * Endpoints:
 *   GET  /api/admin/exam-purchases/stats                    — summary stats (startDate, endDate)
 *   GET  /api/admin/exam-purchases                          — list (page, limit, search, sort,
 *                                                                    paymentStatus, fulfillmentStatus,
 *                                                                    emailStatus, startDate, endDate)
 *   GET  /api/admin/exam-purchases/:id                      — single purchase detail
 *   POST /api/admin/exam-purchases/:id/retry-fulfillment    — retry delivery of a purchase
 *   GET  /api/admin/exam-dashboard                          — dashboard metrics (recentPurchasesLimit,
 *                                                                    recentFailedEmailsLimit, examType)
 */
import { authHeaders, resolveToken } from "./adminUtils";
import { toDisplayString } from "../../utils/toDisplayString";
import { API_URL } from "../../constants";

export const examPurchaseInitialState = {
  // List
  examPurchases: [],
  examPurchasesLoading: false,
  examPurchasesError: null,
  examPurchasesTotal: 0,
  examPurchasesPages: 1,

  // Single purchase
  examPurchaseDetail: null,
  examPurchaseDetailLoading: false,
  examPurchaseDetailError: null,

  // Stats
  examPurchaseStats: null,
  examPurchaseStatsLoading: false,
  examPurchaseStatsError: null,

  // Dashboard
  examDashboard: null,
  examDashboardLoading: false,
  examDashboardError: null,
  // Cached dashboard snapshots keyed by exam-type/limits, so the card can render
  // instantly while a fresh response is always fetched in the background.
  examDashboardCache: {},

  // Mutation
  examPurchaseMutationLoading: false,
  examPurchaseMutationError: null,
};

/** Normalize any list payload shape into `{ items, total, totalPages }`. */
function normalizeListPayload(data) {
  const payload = data?.data || data || {};
  if (Array.isArray(payload)) {
    return { items: payload, total: payload.length, totalPages: 1 };
  }
  return {
    items: payload.items || payload.examPurchases || payload.purchases || [],
    total: payload.total ?? payload.totalItems ?? 0,
    totalPages: payload.totalPages ?? payload.pages ?? 1,
  };
}

/** Extract the exam type from a purchase reference (e.g. "NECO-PIN-..." -> "NECO"). */
function deriveExamType(reference) {
  const match = String(reference || "").toUpperCase().match(/^([A-Z]+)/);
  if (!match) return "";
  const code = match[1];
  return ["WAEC", "NECO", "JAMB", "NABTEB", "GCE"].includes(code) ? code : "";
}

/**
 * Flatten the exam-purchase API payload returned by
 * GET /api/admin/exam-purchases(/:id) into the flat shape the admin UI renders.
 *
 * Real payload (nested):
 *   { id, reference, amount, currency, status,
 *     purchaser: { name, email, phone },
 *     payment: { reference, gatewayReference, status, paidAt, verifiedAt },
 *     fulfillment: { status }, email: { status }, createdAt, updatedAt }
 *
 * Normalized (flat):
 *   { ...raw, customerName, customerEmail, phone,
 *     paymentStatus, fulfillmentStatus, emailStatus, examType }
 *
 * Already-flat fields are kept as fallbacks so older shapes keep working.
 */
function normalizePurchase(raw) {
  if (!raw || typeof raw !== "object") return raw;
  const purchaser = raw.purchaser || {};
  const payment = raw.payment || {};
  const fulfillment = raw.fulfillment || {};
  const email = raw.email || {};
  const reference = raw.reference || payment.reference || "";
  return {
    ...raw,
    reference,
    customerName: raw.customerName || purchaser.name || raw.name || "",
    customerEmail: raw.customerEmail || purchaser.email || "",
    phone: raw.phone || purchaser.phone || "",
    paymentStatus: payment.status || raw.paymentStatus || raw.status || "",
    fulfillmentStatus: fulfillment.status || raw.fulfillmentStatus || "",
    emailStatus: email.status || raw.emailStatus || "",
    paymentReference: payment.reference || reference,
    gatewayReference: payment.gatewayReference ?? raw.gatewayReference ?? null,
    paidAt: payment.paidAt ?? raw.paidAt ?? null,
    verifiedAt: payment.verifiedAt ?? raw.verifiedAt ?? null,
    examType: raw.examType || deriveExamType(reference),
  };
}

/** Per-filter cache key so each exam-type snapshot is stored separately. */
function examDashboardCacheKey(params = {}) {
  const { examType = "", recentPurchasesLimit = "", recentFailedEmailsLimit = "" } = params;
  return [examType || "ALL", recentPurchasesLimit, recentFailedEmailsLimit].join("|");
}

/** How long an exam-dashboard snapshot stays fresh before it revalidates. */
export const EXAM_DASHBOARD_CACHE_TTL_MS = 60 * 1000;

function apiUrl(path) {
  if (!API_URL) {
    throw new Error("REACT_APP_API_BASE_URL is not configured.");
  }
  return `${API_URL}${path}`;
}

async function parseJsonResponse(response) {
  const text = await response.text();
  const contentType = response.headers.get("content-type") || "";

  if (!text) return {};
  if (contentType.includes("application/json")) return JSON.parse(text);
  if (text.trim().startsWith("<")) {
    const error = new Error(`API returned HTML instead of JSON for ${response.url}. Check the API base URL or route.`);
    error.isHtmlResponse = true;
    throw error;
  }

  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`API returned a non-JSON response for ${response.url}.`);
  }
}

async function fetchJsonWithFallback(paths, options) {
  let lastError = null;

  for (const path of paths) {
    try {
      const response = await fetch(apiUrl(path), options);
      const data = await parseJsonResponse(response);

      if (response.status !== 404) {
        return { response, data };
      }

      lastError = new Error(toDisplayString(data.error || data.message, `Route not found: ${response.url}`));
    } catch (err) {
      lastError = err;
      if (!err.isHtmlResponse) throw err;
    }
  }

  throw lastError || new Error("No matching exam purchase endpoint was found.");
}

export function createExamPurchaseActions(set, get) {
  return {
    // ── GET /api/admin/exam-purchases ──
    fetchExamPurchases: async (params = {}) => {
      set({ examPurchasesLoading: true, examPurchasesError: null });
      try {
        const query = new URLSearchParams(
          Object.entries(params).filter(([, v]) => v !== undefined && v !== "")
        ).toString();
        const { response, data } = await fetchJsonWithFallback(
          [
            // non-admin route removed; the list endpoint lives under /api/admin
            `/api/admin/exam-purchases${query ? `?${query}` : ""}`,
          ],
          { headers: authHeaders(resolveToken(get)) }
        );
        if (!response.ok || data.error) {
          const error = toDisplayString(data.error || data.message, "Failed to fetch exam purchases");
          set({ examPurchasesLoading: false, examPurchasesError: error });
          return { success: false, error };
        }
        const { items, total, totalPages } = normalizeListPayload(data);
        set({
          examPurchasesLoading: false,
          examPurchases: items.map(normalizePurchase),
          examPurchasesTotal: total,
          examPurchasesPages: totalPages,
        });
        return { success: true, purchases: items.map(normalizePurchase), total, pages: totalPages };
      } catch (err) {
        set({
          examPurchasesLoading: false,
          examPurchasesError: err.message,
          examPurchases: [],
        });
        return { success: false, error: err.message };
      }
    },

    // ── GET /api/admin/exam-purchases/:id ──
    fetchExamPurchaseById: async (id) => {
      set({ examPurchaseDetailLoading: true, examPurchaseDetailError: null });
      try {
        const { response, data } = await fetchJsonWithFallback(
          [`/api/admin/exam-purchases/${id}`],
          { headers: authHeaders(resolveToken(get)) }
        );
        if (!response.ok || data.error) {
          const error = toDisplayString(data.error || data.message, "Failed to fetch exam purchase");
          set({ examPurchaseDetailLoading: false, examPurchaseDetailError: error });
          return { success: false, error };
        }
        const detail = normalizePurchase(data?.data || data || null);
        set({ examPurchaseDetailLoading: false, examPurchaseDetail: detail });
        return { success: true, purchase: detail };
      } catch (err) {
        set({
          examPurchaseDetailLoading: false,
          examPurchaseDetailError: err.message,
          examPurchaseDetail: null,
        });
        return { success: false, error: err.message };
      }
    },

    // ── POST /api/admin/exam-purchases/:id/retry-fulfillment ──
    retryExamPurchaseFulfillment: async (id) => {
      set({ examPurchaseMutationLoading: true, examPurchaseMutationError: null });
      try {
        const { response, data } = await fetchJsonWithFallback(
          [
            // non-admin route removed; the retry endpoint lives under /api/admin
            `/api/admin/exam-purchases/${id}/retry-fulfillment`,
          ],
          { method: "POST", headers: authHeaders(resolveToken(get)) }
        );
        if (!response.ok || data.error) {
          const error = toDisplayString(data.error || data.message, "Failed to retry fulfillment");
          set({ examPurchaseMutationLoading: false, examPurchaseMutationError: error });
          return { success: false, error };
        }
        set({
          examPurchaseMutationLoading: false,
          successMessage: "Fulfillment retried successfully",
        });
        return { success: true, data: data?.data || data };
      } catch (err) {
        set({
          examPurchaseMutationLoading: false,
          examPurchaseMutationError: err.message,
        });
        return { success: false, error: err.message };
      }
    },

    // ── GET /api/admin/exam-purchases/stats ──
    fetchExamPurchaseStats: async ({ startDate, endDate } = {}) => {
      set({ examPurchaseStatsLoading: true, examPurchaseStatsError: null });
      try {
        const params = [];
        if (startDate) params.push(`startDate=${encodeURIComponent(startDate)}`);
        if (endDate) params.push(`endDate=${encodeURIComponent(endDate)}`);
        const query = params.length ? `?${params.join("&")}` : "";
        const { response, data } = await fetchJsonWithFallback(
          [`/api/admin/exam-purchases/stats${query}`],
          { headers: authHeaders(resolveToken(get)) }
        );
        if (!response.ok || data.error) {
          const error = toDisplayString(data.error || data.message, "Failed to fetch exam purchase stats");
          set({ examPurchaseStatsLoading: false, examPurchaseStatsError: error });
          return { success: false, error };
        }
        const stats = data?.data || data || null;
        set({ examPurchaseStatsLoading: false, examPurchaseStats: stats });
        return { success: true, stats };
      } catch (err) {
        set({ examPurchaseStatsLoading: false, examPurchaseStatsError: err.message, examPurchaseStats: null });
        return { success: false, error: err.message };
      }
    },

    // ── GET /api/admin/exam-dashboard ──
    // Cache-first: a fresh cached snapshot for this filter is reused with no
    // request. Once it goes stale the card renders the cached data instantly and
    // revalidates in the background, so new numbers always land.
    fetchExamDashboard: async (
      params = {},
      { force = false, ttlMs = EXAM_DASHBOARD_CACHE_TTL_MS } = {}
    ) => {
      const cacheKey = examDashboardCacheKey(params);
      const cached = get().examDashboardCache?.[cacheKey];

      // Fresh cache → reuse it, no network call.
      if (!force && cached && Date.now() - cached.fetchedAt < ttlMs) {
        set({
          examDashboard: cached.data,
          examDashboardLoading: false,
          examDashboardError: null,
        });
        return { success: true, dashboard: cached.data, cached: true };
      }

      set({
        // Silent revalidation when we already have something to show.
        examDashboardLoading: !cached,
        examDashboardError: null,
        // Show cached data for this filter instantly; null (loading) if none yet.
        examDashboard: cached ? cached.data : null,
      });

      try {
        const query = new URLSearchParams(
          Object.entries(params).filter(
            ([, v]) => v !== undefined && v !== null && v !== ""
          )
        ).toString();
        const { response, data } = await fetchJsonWithFallback(
          [`/api/admin/exam-dashboard${query ? `?${query}` : ""}`],
          { headers: authHeaders(resolveToken(get)) }
        );
        if (!response.ok || data.error) {
          const error = toDisplayString(data.error || data.message, "Failed to fetch exam dashboard");
          set({ examDashboardLoading: false, examDashboardError: error });
          return { success: false, error };
        }
        const dashboard = data?.data || data || null;
        set((state) => ({
          examDashboardLoading: false,
          examDashboard: dashboard,
          examDashboardCache: {
            ...(state.examDashboardCache || {}),
            [cacheKey]: { data: dashboard, fetchedAt: Date.now() },
          },
        }));
        return { success: true, dashboard };
      } catch (err) {
        set({ examDashboardLoading: false, examDashboardError: err.message });
        return { success: false, error: err.message };
      }
    },

    /**
     * Drop every cached exam-dashboard snapshot so the next visit refetches.
     * Triggered automatically whenever an admin mutation succeeds (adminStore).
     */
    invalidateExamDashboard: () => {
      set({ examDashboardCache: {} });
      return { success: true };
    },
  };
}

const examPurchaseActions = createExamPurchaseActions;
export default examPurchaseActions;
